import { useState, useRef, useEffect, useCallback } from "react";
import { Mic, X, Globe, AlertTriangle, Loader2, Languages, Clock, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HARD_CAP_SECONDS = 10 * 60;         // 10-minute hard cap per session
const SILENCE_TIMEOUT_MS = 20 * 1000;     // 20-second silence auto-stop
const LOW_MINUTES_ALERT = 5;              // warn when <5 min left
const VAD_RMS_THRESHOLD = 0.012;          // below this = silence

const LANGUAGES = [
  { code: "", name: "Auto-detect" },
  { code: "en", name: "English" },
  { code: "hi", name: "Hindi" },
  { code: "ta", name: "Tamil" },
  { code: "bn", name: "Bengali" },
  { code: "mr", name: "Marathi" },
  { code: "te", name: "Telugu" },
  { code: "gu", name: "Gujarati" },
  { code: "kn", name: "Kannada" },
  { code: "ml", name: "Malayalam" },
  { code: "pa", name: "Punjabi" },
];

function formatMinutes(mins) {
  if (mins == null) return "—";
  const total = Math.max(0, Math.round(mins * 10) / 10);
  if (total < 1) return `${Math.round(total * 60)}s`;
  return `${Math.round(total)}m`;
}

function formatElapsed(sec) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const PremiumVoiceRecorder = ({ onClose, onTranscript }) => {
  const [ledger, setLedger] = useState(null);
  const [loadingLedger, setLoadingLedger] = useState(true);
  const [language, setLanguage] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [silenceCountdown, setSilenceCountdown] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const silenceStartRef = useRef(null);
  const hardCapHitRef = useRef(false);
  const lowAlertFiredRef = useRef(false);

  // Fetch ledger on mount
  const fetchLedger = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/premium-voice/ledger`);
      setLedger(res.data);
    } catch {
      toast.error("Couldn't fetch your minute balance");
    } finally {
      setLoadingLedger(false);
    }
  }, []);

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  const cleanup = useCallback(() => {
    clearInterval(elapsedTimerRef.current);
    cancelAnimationFrame(rafRef.current);
    try { mediaRecorderRef.current?.stop?.(); } catch { /* ignore */ }
    try { streamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch { /* ignore */ }
    const ctx = audioCtxRef.current;
    if (ctx && ctx.state !== "closed") {
      try { ctx.close().catch(() => {}); } catch { /* ignore */ }
    }
    mediaRecorderRef.current = null;
    streamRef.current = null;
    audioCtxRef.current = null;
    analyserRef.current = null;
    silenceStartRef.current = null;
    setElapsed(0);
    setSilenceCountdown(0);
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const stopRecording = useCallback((reason = "manual") => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === "inactive") return;
    hardCapHitRef.current = reason === "hard_cap";
    try { mediaRecorderRef.current.stop(); } catch { /* ignore */ }
    if (reason === "hard_cap") {
      toast.info("10-minute session limit reached. Your audio was saved.", { duration: 6000 });
    } else if (reason === "silence") {
      toast.info("Auto-stopped: 20 seconds of silence detected.", { duration: 5000 });
    } else if (reason === "out_of_minutes") {
      toast.error("No premium minutes left. Please top up to continue.");
    }
  }, []);

  // Voice Activity Detection + per-frame silence accounting
  const vadLoop = useCallback(() => {
    if (!analyserRef.current) return;
    const buf = new Float32Array(analyserRef.current.fftSize);
    analyserRef.current.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    const rms = Math.sqrt(sum / buf.length);

    if (rms > VAD_RMS_THRESHOLD) {
      silenceStartRef.current = null;
      setSilenceCountdown(0);
    } else {
      if (silenceStartRef.current === null) {
        silenceStartRef.current = performance.now();
      }
      const silentFor = performance.now() - silenceStartRef.current;
      setSilenceCountdown(Math.max(0, Math.floor((SILENCE_TIMEOUT_MS - silentFor) / 1000)));
      if (silentFor >= SILENCE_TIMEOUT_MS) {
        stopRecording("silence");
        return;
      }
    }
    rafRef.current = requestAnimationFrame(vadLoop);
  }, [stopRecording]);

  const startRecording = async () => {
    if (!ledger || ledger.minutes_available <= 0) {
      toast.error("No premium minutes left. Please top up to continue.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      hardCapHitRef.current = false;
      lowAlertFiredRef.current = ledger.minutes_available < LOW_MINUTES_ALERT;
      if (lowAlertFiredRef.current) {
        toast.warning(`Only ${formatMinutes(ledger.minutes_available)} of premium time remaining.`, { duration: 6000 });
      }

      // Audio context for VAD
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      analyserRef.current = analyser;

      // MediaRecorder — pick the best supported codec
      const preferredMimes = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ];
      const mimeType = preferredMimes.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        cancelAnimationFrame(rafRef.current);
        clearInterval(elapsedTimerRef.current);
        setIsRecording(false);
        const blob = new Blob(chunksRef.current, { type: mimeType || "audio/webm" });
        chunksRef.current = [];
        try { streamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch { /* ignore */ }
        const ctx = audioCtxRef.current;
        if (ctx && ctx.state !== "closed") {
          try { await ctx.close(); } catch { /* ignore */ }
        }
        audioCtxRef.current = null;
        analyserRef.current = null;
        if (blob.size === 0) return;
        await uploadAudio(blob, mimeType);
      };
      mr.start();
      setIsRecording(true);
      setElapsed(0);

      // Elapsed timer + hard cap
      elapsedTimerRef.current = setInterval(() => {
        setElapsed((e) => {
          const next = e + 1;
          if (next >= HARD_CAP_SECONDS) {
            stopRecording("hard_cap");
          }
          return next;
        });
      }, 1000);

      // Start VAD
      rafRef.current = requestAnimationFrame(vadLoop);
    } catch (err) {
      toast.error(`Microphone error: ${err.message || err.name}`);
      cleanup();
    }
  };

  const uploadAudio = async (blob, mimeType) => {
    setUploading(true);
    const tid = toast.loading("Transcribing with Whisper-1…");
    try {
      const ext = (mimeType || "audio/webm").includes("mp4") ? "m4a"
                 : (mimeType || "audio/webm").includes("ogg") ? "ogg"
                 : "webm";
      const form = new FormData();
      form.append("audio", blob, `premium.${ext}`);
      if (language) form.append("language", language);

      const res = await axios.post(`${API}/premium-voice/transcribe`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000,
      });
      const { text, minutes_charged, minutes_available, language: detected } = res.data;
      // Re-fetch the full ledger so the "Last 5 transcriptions" list refreshes with the new entry
      fetchLedger();
      toast.success(
        `Transcribed${detected ? ` (${detected})` : ""}. ${formatMinutes(minutes_charged)} used, ${formatMinutes(minutes_available)} left.`,
        { id: tid, duration: 5000 }
      );
      if (text && text.trim()) {
        onTranscript?.(text.trim());
        onClose?.();
      } else {
        toast.info("No speech detected in the recording.", { id: tid });
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Transcription failed";
      toast.error(detail, { id: tid });
    } finally {
      setUploading(false);
    }
  };

  const available = ledger?.minutes_available ?? 0;
  const purchased = ledger?.minutes_purchased ?? 0;
  const pct = purchased > 0 ? Math.max(0, Math.min(100, (available / purchased) * 100)) : 0;
  const isLow = available > 0 && available < LOW_MINUTES_ALERT;
  const isEmpty = available <= 0;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4" onClick={() => !isRecording && onClose?.()} data-testid="premium-voice-recorder">
      <div className="bg-[#F8F6F1] w-full md:max-w-md md:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#2D3A2D] to-[#4A5D4A] text-white p-5">
          <button onClick={onClose} disabled={isRecording} className="absolute top-4 right-4 p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition disabled:opacity-40" data-testid="close-premium-voice">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <Globe className="w-4 h-4 text-amber-300" />
            <span className="text-[10px] font-sans uppercase tracking-[0.2em] text-amber-300/90">Premium • Whisper-1</span>
          </div>
          <h2 className="font-serif text-2xl mb-0.5">Multi-Language Voice</h2>
          <p className="font-sans text-xs text-white/70">Speak in Hindi, Tamil, Hinglish, or any of 50+ languages.</p>
        </div>

        {/* Ledger */}
        <div className="p-5 border-b border-[#D4C9B8]/30" data-testid="ledger-panel">
          <div className="flex items-baseline justify-between mb-2">
            <span className="font-sans text-[10px] uppercase tracking-wider text-[#7A8A7A]">Available</span>
            <span className="font-sans text-xs text-[#7A8A7A]" data-testid="ledger-text">
              {loadingLedger ? "…" : (
                <>
                  <span className="font-semibold text-[#2D3A2D]" data-testid="minutes-available">{formatMinutes(available)}</span>
                  <span> / {formatMinutes(purchased)}</span>
                </>
              )}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-[#D4C9B8]/50 overflow-hidden">
            <div
              className={`h-full transition-all ${isEmpty ? "bg-red-400" : isLow ? "bg-amber-400" : "bg-[#7A9B7E]"}`}
              style={{ width: `${pct}%` }}
              data-testid="ledger-bar"
            />
          </div>
          {isLow && !isEmpty && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5" data-testid="low-minutes-alert">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-sans text-[11px] font-semibold text-amber-800">Running low</p>
                <p className="font-sans text-[10px] text-amber-700">Only {formatMinutes(available)} of premium time remaining.</p>
              </div>
            </div>
          )}
          {isEmpty && (
            <div className="mt-3 rounded-lg bg-red-50 border border-red-200 p-2.5" data-testid="empty-minutes-alert">
              <p className="font-sans text-[11px] font-semibold text-red-800 mb-0.5">Out of minutes</p>
              <p className="font-sans text-[10px] text-red-700">Top up to continue using premium voice.</p>
            </div>
          )}

          {/* Last 5 transcriptions log */}
          {ledger?.recent_transcriptions?.length > 0 && (
            <div className="mt-3" data-testid="transcription-log">
              <button
                onClick={() => setHistoryOpen((o) => !o)}
                className="flex items-center justify-between w-full text-left group"
                data-testid="transcription-log-toggle"
              >
                <span className="flex items-center gap-1.5 text-[10px] font-sans uppercase tracking-wider text-[#7A8A7A] group-hover:text-[#4A5D4A] transition-colors">
                  <Clock className="w-3 h-3" />
                  Last {ledger.recent_transcriptions.length} transcription{ledger.recent_transcriptions.length === 1 ? "" : "s"}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-[#7A8A7A] transition-transform ${historyOpen ? "rotate-180" : ""}`} />
              </button>
              {historyOpen && (
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto" data-testid="transcription-log-list">
                  {ledger.recent_transcriptions.map((t, i) => {
                    const when = t.timestamp ? new Date(t.timestamp) : null;
                    const label = when ? when.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                    return (
                      <div key={i} className="flex items-start justify-between gap-2 rounded-md bg-white/70 px-2 py-1.5 border border-[#D4C9B8]/40" data-testid={`transcription-log-item-${i}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-sans text-[10px] text-[#4A5D4A] truncate">
                            {t.preview || <span className="italic text-[#9FB89F]">(no text captured)</span>}
                          </p>
                          <p className="font-sans text-[9px] text-[#9FB89F]">
                            {label}
                            {t.language ? <> · <span className="uppercase">{t.language}</span></> : null}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-sans text-[10px] font-semibold text-[#2D3A2D]">{formatMinutes(t.minutes_charged)}</p>
                          <p className="font-sans text-[9px] text-[#9FB89F]">{Math.round(t.duration_seconds || 0)}s</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Language picker */}
        <div className="p-5 border-b border-[#D4C9B8]/30">
          <label className="flex items-center gap-2 mb-2">
            <Languages className="w-3.5 h-3.5 text-[#7A9B7E]" />
            <span className="font-sans text-[10px] uppercase tracking-wider text-[#7A8A7A]">Language hint (optional)</span>
          </label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            disabled={isRecording}
            className="w-full px-3 py-2 rounded-lg bg-white border border-[#D4C9B8]/50 text-sm font-sans text-[#2D3A2D] focus:outline-none focus:ring-1 focus:ring-[#7A9B7E] disabled:opacity-50"
            data-testid="language-select"
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>{l.name}</option>
            ))}
          </select>
        </div>

        {/* Recorder */}
        <div className="p-6 flex flex-col items-center">
          <button
            onClick={isRecording ? () => stopRecording("manual") : startRecording}
            disabled={uploading || loadingLedger || isEmpty}
            className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
              isRecording ? "bg-red-500 hover:bg-red-600 animate-pulse" : "bg-[#4A5D4A] hover:bg-[#2D3A2D]"
            }`}
            data-testid="premium-record-button"
          >
            {uploading ? <Loader2 className="w-7 h-7 text-white animate-spin" /> : <Mic className="w-8 h-8 text-white" />}
          </button>

          {isRecording && (
            <div className="mt-4 text-center space-y-1" data-testid="recording-info">
              <p className="font-serif text-xl text-[#2D3A2D]" data-testid="elapsed-time">{formatElapsed(elapsed)}</p>
              <p className="font-sans text-[10px] uppercase tracking-wider text-red-500">
                Recording • {HARD_CAP_SECONDS / 60}-min cap
              </p>
              {silenceCountdown > 0 && silenceCountdown <= 10 && (
                <p className="font-sans text-[10px] text-amber-600" data-testid="silence-warning">
                  Silent — auto-stop in {silenceCountdown}s
                </p>
              )}
            </div>
          )}
          {!isRecording && !uploading && (
            <p className="mt-3 font-sans text-xs text-[#7A8A7A] text-center">
              {isEmpty ? "Out of minutes" : "Tap to record"}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 text-center">
          <p className="font-sans text-[10px] text-[#9FB89F]">
            Auto-stops at 10 min, or after 20s of silence.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PremiumVoiceRecorder;
